import { request } from './client.js';
import type {
  ClientTenant,
  ConnectSecureCredentialInput,
  ListIntegrationsResult,
  ListVendorAccountsResult,
  NinjaOneCredentialInput,
  PlatformIntegrationName,
  PlatformIntegrationStatus,
} from './types.js';

/**
 * The platform's integrations — the connector registry with each one's
 * declared capabilities (task 69). Needs no Client: it is the catalog,
 * not a Client's state, which lives on `listTenants`.
 */
export function listIntegrations(idToken: string): Promise<ListIntegrationsResult> {
  return request<ListIntegrationsResult>('GET', '/integrations', idToken);
}

// ---------- NinjaOne and ConnectSecure (task 161) ----------

/** Whether the platform credential is stored, when it changed, and how many Clients are mapped. */
export function getPlatformIntegration(
  name: PlatformIntegrationName,
  idToken: string,
): Promise<PlatformIntegrationStatus> {
  return request<PlatformIntegrationStatus>('GET', `/integrations/${name}`, idToken);
}

/**
 * Store the platform credential. The API validates it against the vendor
 * first (422 `validation_failed` with the vendor's reason) and never returns it.
 */
export function putPlatformCredential(
  name: 'ninjaone',
  credential: NinjaOneCredentialInput,
  idToken: string,
): Promise<PlatformIntegrationStatus>;
export function putPlatformCredential(
  name: 'connectsecure',
  credential: ConnectSecureCredentialInput,
  idToken: string,
): Promise<PlatformIntegrationStatus>;
export function putPlatformCredential(
  name: PlatformIntegrationName,
  credential: NinjaOneCredentialInput | ConnectSecureCredentialInput,
  idToken: string,
): Promise<PlatformIntegrationStatus> {
  return request<PlatformIntegrationStatus>('PUT', `/integrations/${name}/credential`, idToken, {
    body: credential,
  });
}

/** Remove the platform credential. 409 `clients_mapped` while any Client is mapped. */
export function deletePlatformCredential(
  name: PlatformIntegrationName,
  idToken: string,
): Promise<PlatformIntegrationStatus> {
  return request<PlatformIntegrationStatus>('DELETE', `/integrations/${name}/credential`, idToken);
}

/** The vendor's organizations (NinjaOne) or companies (ConnectSecure), each with who holds it. */
export function listVendorAccounts(
  name: PlatformIntegrationName,
  idToken: string,
): Promise<ListVendorAccountsResult> {
  const path = name === 'ninjaone' ? 'organizations' : 'companies';
  return request<ListVendorAccountsResult>('GET', `/integrations/${name}/${path}`, idToken);
}

/**
 * Map a Client to one vendor organization or company. 409 `already_mapped`
 * when another Client holds it; 422 when the vendor does not list it.
 * Unmapping is `removeSource(clientTenantId, name)`.
 */
export function putSourceMapping(
  clientTenantId: string,
  name: PlatformIntegrationName,
  externalId: string,
  idToken: string,
  options: { addService?: boolean } = {},
): Promise<ClientTenant> {
  const key = name === 'ninjaone' ? 'organization_id' : 'company_id';
  return request<ClientTenant>(
    'PUT',
    `/tenants/${encodeURIComponent(clientTenantId)}/sources/${name}`,
    idToken,
    {
      body: { [key]: externalId },
      // A platform Admin's confirm after 409 `service_required` (task 179).
      query: options.addService ? { add_service: 'true' } : undefined,
    },
  );
}
